/**
 * Pending-legal-document resolution (ADR-086 — soft consent modal; brand
 * dimension added ADR-088).
 *
 * Pure, sync, zero-DB comparator: given a user's cached consent state,
 * returns the documents whose cached version is missing or stale FOR THE
 * REQUESTED BRAND. Single source of truth for this comparison — consumed by
 * BOTH the `legal` BC (ledger read / accept context) and the `auth` BC
 * (`GET /auth/me`'s `legal.pending`), so the two can never drift.
 *
 * @see docs/specifications/2026-07-03-legal-consent-soft-modal/spec.md A1 (DRY radar)
 * @see docs/specifications/2026-07-06-per-brand-legal-content/spec.md AC A, C
 * @see docs/architecture/adr/088-per-brand-legal-content-distribution.md
 */
import { BrandSlug, FALLBACK_BRAND_SLUG } from './brand';
import { LEGAL_DOCUMENT_TYPES, LegalDocumentType } from './version-history';
import { getLegalContent } from './registry';

export { LEGAL_DOCUMENT_URLS_BY_BRAND, LEGAL_DOCUMENT_URLS } from './legal-document-urls';

/** A document the user still has to (re-)accept, for a specific brand. */
export interface PendingDoc {
  documentType: LegalDocumentType;
  brandSlug: BrandSlug;
  currentVersion: string;
  url: string;
}

/** One accepted document's cached version/timestamp (mirrors `apps/backend`'s `LegalStateDocEntry`, ADR-088). */
export interface LegalStateDocEntry {
  version: string;
  acceptedAt: string;
}

/** Per-brand slice of the cache — either document may be absent (mirrors `LegalStateBrandEntry`). */
export interface LegalStateBrandEntry {
  terms?: LegalStateDocEntry;
  privacy?: LegalStateDocEntry;
}

/**
 * Minimal shape needed for the sync in-memory pending check — mirrors the
 * cache columns already loaded onto the user (e.g. by `JwtStrategy.validate()`
 * / `usersService.findById`). Deliberately NOT the `User` entity itself.
 *
 * `termsVersion`/`privacyVersion` — the pre-ADR-088 flat, single-brand cache.
 * `legalState` — the per-brand cache (ADR-088 D3): a user MAY belong to
 * tenants of BOTH brands, so a single flat version can't represent "current
 * for brand A AND brand B" at once. When absent (legacy user / caller not yet
 * migrated), `resolvePendingFromCache` falls back to the flat fields, treated
 * as the `kiedynabory` entry (backward compat — historically the only brand).
 */
export interface UserVersionCache {
  termsVersion: string | null;
  privacyVersion: string | null;
  legalState?: Record<string, LegalStateBrandEntry> | null;
}

/** Reads the cached (documentType -> version | null) pair for `brand`, with the legacy flat-column fallback (see `UserVersionCache` JSDoc). */
function readCachedVersions(
  user: UserVersionCache | null | undefined,
  brand: BrandSlug,
): Record<LegalDocumentType, string | null> {
  const legalState = user?.legalState;
  if (legalState) {
    const brandState = legalState[brand];
    return {
      terms: brandState?.terms?.version ?? null,
      privacy: brandState?.privacy?.version ?? null,
    };
  }
  // No per-brand cache at all — legacy shape. Only valid as a `kiedynabory`
  // proxy (that's the only brand the flat columns ever tracked); any other
  // brand has no legacy data to fall back to.
  if (brand === FALLBACK_BRAND_SLUG) {
    return {
      terms: user?.termsVersion ?? null,
      privacy: user?.privacyVersion ?? null,
    };
  }
  return { terms: null, privacy: null };
}

/**
 * SYNC, in-memory, zero-DB. Returns the documents whose CURRENT version
 * (for `brandSlug`) differs from the version cached on the user (missing
 * cache -> pending). `brandSlug` is resolved through the registry
 * (`getLegalContent`) — an unknown/missing brand falls back to `kiedynabory`
 * for this VERSION-comparison path (ADR-088 "Failure mode"), it never throws.
 *
 * Fail-open (ADR-086/ADR-088): this function MUST NOT throw — `GET /auth/me`
 * must never 500 because of a malformed `user`/`brandSlug`. Any unexpected
 * error is swallowed and treated as "nothing pending" (the same fail-open
 * default the `auth` BC's own wrapper uses).
 */
export function resolvePendingFromCache(
  user: UserVersionCache,
  brandSlug?: string | null,
): PendingDoc[] {
  try {
    const content = getLegalContent(brandSlug);
    const resolvedBrand = content.brandSlug;
    const cached = readCachedVersions(user, resolvedBrand);

    return LEGAL_DOCUMENT_TYPES.filter((docType) => {
      const version = cached[docType];
      return version === null || !content.isVersionCurrent(docType, version);
    }).map((docType) => ({
      documentType: docType,
      brandSlug: resolvedBrand,
      currentVersion: content.currentVersion(docType),
      url: content.urls[docType],
    }));
  } catch {
    return [];
  }
}
